import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { Flex, Heading } from '@radix-ui/themes';
import { Accordion, Callout, PillToggle } from '@/components/common';
import { WizardScreen } from '@/components/layout';
import { ContinueButton, OnboardingWizardHeader } from '@/components/onboarding';
import {
  V90_OPEN_TO_OPTIONS,
  V90_RELATIONSHIP_STATUS_OPTIONS,
  V90_RELATIONSHIP_TYPE_OPTIONS,
} from '@/constants';
import { useOnboardingStepSchemas, validateOnboardingStep } from '@/lib/validators';
import { useOnboardingStore } from '@/stores/onboardingStore';
import type { OnboardingStepProps } from './types';
import { useSaveAndNext } from './useSaveAndNext';

export function SituationStep({ flow }: OnboardingStepProps) {
  const { t } = useTranslation('onboarding');
  const selectedOpenTo = useOnboardingStore(state => state.selectedOpenTo);
  const setSelectedOpenTo = useOnboardingStore(state => state.setSelectedOpenTo);
  const selectedRelationshipTypes = useOnboardingStore(state => state.selectedRelationshipTypes);
  const setSelectedRelationshipTypes = useOnboardingStore(
    state => state.setSelectedRelationshipTypes
  );
  const relationshipStatus = useOnboardingStore(state => state.relationshipStatus);
  const setRelationshipStatus = useOnboardingStore(state => state.setRelationshipStatus);
  const saveAndNext = useSaveAndNext(flow);
  const stepSchemas = useOnboardingStepSchemas();
  const isValid = useMemo(
    () =>
      validateOnboardingStep(
        flow.step,
        { selectedOpenTo, selectedRelationshipTypes, relationshipStatus },
        stepSchemas
      ).isValid,
    [selectedOpenTo, selectedRelationshipTypes, relationshipStatus, flow.step, stepSchemas]
  );

  return (
    <WizardScreen header={<OnboardingWizardHeader flow={flow} />}>
      <Flex direction="column" gap="4" py="2" pb="5">
        <Heading size="5" weight="light">
          {t('steps.situation.title')}
        </Heading>
        <Callout>{t('steps.situation.callout')}</Callout>
        <Accordion type="multiple" gap="4" className="spot-cat-accordion--brand">
          <Accordion.Item
            value="openTo"
            title={t('steps.situation.openTo')}
            subtitle={t('steps.situation.openToTag')}
            badge={selectedOpenTo.length}
          >
            <PillToggle
              type="multiple"
              options={V90_OPEN_TO_OPTIONS}
              value={selectedOpenTo}
              onValueChange={setSelectedOpenTo}
              dataTestId="open-to"
            />
          </Accordion.Item>
          <Accordion.Item
            value="relType"
            title={t('steps.situation.relationshipType')}
            subtitle={t('steps.situation.relationshipTypeTag')}
            badge={selectedRelationshipTypes.length}
          >
            <PillToggle
              type="multiple"
              options={V90_RELATIONSHIP_TYPE_OPTIONS}
              value={selectedRelationshipTypes}
              onValueChange={setSelectedRelationshipTypes}
              dataTestId="relationship-type"
            />
          </Accordion.Item>
          <Accordion.Item
            value="status"
            title={t('steps.situation.status')}
            subtitle={t('steps.situation.statusTag')}
            badge={relationshipStatus ? 1 : 0}
          >
            <PillToggle
              type="single"
              options={V90_RELATIONSHIP_STATUS_OPTIONS}
              value={relationshipStatus}
              onValueChange={setRelationshipStatus}
              dataTestId="status"
            />
          </Accordion.Item>
        </Accordion>
        <ContinueButton onClick={saveAndNext} disabled={!isValid}>
          {t('actions.continue')}
        </ContinueButton>
      </Flex>
    </WizardScreen>
  );
}
